import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Button from "src/Components/Button/Button";
import { InfoMessageType } from "src/Components/InfoBar/InfoBar";
import PopupDialog from "src/SharedComponents/PopupDialog/PopupDialog";
import { useGetResubscribeInfo } from "src/Utils/Hooks/Payments/useGetResubscribeInfo";
import { formatDayMonthYear } from "src/Utils/DateFormatting/DateFormattter";
import { formatMoney } from "src/Utils/CurrencySymbol/formatMoney";

interface IResubscribeInfoProps {
    clinicId: string;
    priceId: string;
    onClose: () => void;
    setInfo: (message: string, type: InfoMessageType) => void;
    updatePageLoader: (load: boolean) => void;
    onConfirm: () => void;
}

/**
 * Maps the licensing service's interval to a translation key.
 *
 * The service sends the raw enum name ("monthly"/"yearly"), which must not be interpolated into a
 * sentence: it is English, so a Chinese string would read "按monthly计费". These keys carry the whole
 * phrase, including the start date the design asks for, in both locales.
 */
const BILLED_INTERVAL_KEYS: Record<string, string> = {
    monthly: 'MonthlyBilledStarting',
    yearly: 'AnnuallyBilledStarting',
};

/**
 * Confirms what a returning clinic is about to pay, before it is sent to the payment page.
 *
 * A clinic cancelled for non-payment kept working through the grace window, so re-subscribing
 * charges two things: the arrears for those days and the first period of the new subscription.
 * Showing that breakdown here rather than letting a larger-than-expected total appear for the first
 * time on the payment page is the whole point of the dialog - the arrears are for access already
 * consumed, which needs saying in words before it appears as a number.
 *
 * Deliberately shows no tax line. The provider calculates tax at checkout against the customer's
 * billing address; anything shown here would be a guess that could disagree with the invoice.
 */
const ResubscribeInfo: React.FC<IResubscribeInfoProps> = ({ clinicId, priceId, onClose, setInfo, updatePageLoader, onConfirm }) => {
    const { t } = useTranslation();
    const { getResubscribeInfo, data, loading, error } = useGetResubscribeInfo();

    useEffect(() => {
        getResubscribeInfo(clinicId, priceId);
    }, [clinicId, priceId, getResubscribeInfo]);

    useEffect(() => {
        updatePageLoader(loading);
    }, [loading, updatePageLoader]);

    useEffect(() => {
        if (error) {
            // The quote failed, so there is no trustworthy total to show. Closing rather than
            // offering a Confirm button matters: the backend blocks the checkout on this same
            // failure, so a dialog that let the customer proceed would only move the error one
            // click later.
            setInfo(error, InfoMessageType.error);
            onClose();
        }
    }, [error, setInfo, onClose]);

    // Nothing owed means there is nothing to confirm - go straight to the payment page, which is
    // what re-subscribing did before this dialog existed.
    //
    // Latched, because this fires from an effect rather than a click: any re-render that changes
    // the identity of `data` or `onConfirm` would run it again, and each run POSTs a checkout
    // session. One stray double-fire is two Stripe sessions for one customer intent.
    const passedThrough = useRef<boolean>(false);
    useEffect(() => {
        if (data && !data.hasOutstandingPayment && !passedThrough.current) {
            passedThrough.current = true;
            onConfirm();
        }
    }, [data, onConfirm]);

    if (!data || !data.hasOutstandingPayment) {
        return null;
    }

    const money = (minorUnits: number) => formatMoney(minorUnits, data.currency);

    // Absent for anything that is not a recurring interval - a permanent package, say - in which
    // case the line simply carries no subtitle rather than an odd one.
    const billedIntervalKey = BILLED_INTERVAL_KEYS[data.subscriptionInterval?.toLowerCase()];

    // The new subscription starts now, which is what the copy promises ("starts a new subscription
    // today"), so the date is today's rather than anything the service needs to send.
    const startingOn = formatDayMonthYear(new Date().toISOString());

    // Just the dates, per the design. The service's own description ("Aura App access 17 Dec 2026 -
    // 1 Jan 2027 (unpaid)") is the invoice line-item text and repeats the label beside it.
    const unpaidPeriod = data.outstandingAccessFrom && data.outstandingAccessTo
        ? `${formatDayMonthYear(data.outstandingAccessFrom)} – ${formatDayMonthYear(data.outstandingAccessTo)}`
        : data.outstandingDescription;

    return (
        <PopupDialog onClose={onClose} marginTop="200px" popupWidth="496px">
            <div className="p-3">
                <div className="row">
                    <div className="col-md-10">
                        <span style={{ fontWeight: '600', fontSize: '20px' }}>
                            {t('Resubscribe')}
                        </span>
                    </div>
                </div>

                <div className="row mt-4">
                    <span style={{ fontSize: '14px' }}>{t('ResubscribeDuesDesc')}</span>
                </div>

                <ResubscribeLine
                    label={data.subscriptionDescription}
                    detail={billedIntervalKey ? t(billedIntervalKey, { startDate: startingOn }) : ''}
                    value={money(data.subscriptionAmount)}
                />

                <ResubscribeLine
                    label={t('UnpaidAccess')}
                    detail={unpaidPeriod}
                    value={money(data.outstandingAmount)}
                />

                <div
                    className="row mt-2 d-flex"
                    style={{ fontSize: '14px', borderTop: '1px solid #E0E0E0', paddingTop: '25px', marginLeft: '0px', marginRight: '20px', fontWeight: 'bold' }}
                >
                    <span style={{ flex: 2, paddingLeft: 0 }}>{t('AmountDue')}</span>
                    <span className="d-flex" style={{ flex: 1, justifyContent: 'flex-end', paddingRight: 0 }}>
                        {money(data.totalAmount)}
                    </span>
                </div>

                <div className="row mt-5 text-center">
                    <div className="ms-3">
                        <Button
                            fontWeight="500"
                            fontSize="14px"
                            text={t('ConfirmProceed')}
                            onClick={onConfirm}
                        />
                    </div>
                </div>
            </div>
        </PopupDialog>
    );
}

/**
 * One priced line: what it is, an explanatory second line, and the amount.
 *
 * Extracted because the two lines must line up exactly - the arrears sitting a pixel off the
 * subscription price is what makes a two-line total look like a mistake.
 */
const ResubscribeLine: React.FC<{ label: string; detail: string; value: string }> = ({ label, detail, value }) => (
    <>
        <div className="row mt-4 d-flex" style={{ fontSize: '14px' }}>
            <span style={{ flex: 2 }}>{label}</span>
            <span className="d-flex" style={{ flex: 1, justifyContent: 'flex-end' }}>{value}</span>
        </div>
        {detail && (
            <div className="row" style={{ fontSize: '14px' }}>
                <span className="ds-text-primary">{detail}</span>
            </div>
        )}
    </>
);

export default ResubscribeInfo;
